"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ShoppingCartCalculator = void 0;
const constants_1 = require("../constants");
const promotion_1 = require("../promotion");
const shipping_rule_1 = require("../shipping-rule");
const typings_1 = require("../typings");
const utils_1 = require("../utils");
const base_1 = require("./base");
class ShoppingCartCalculator extends base_1.BaseShoppingCart {
    constructor(productSKUs) {
        super(productSKUs);
        this.basePercentage = 100;
        this.minPercentage = 0;
        this.defaultOneTimeAppliedPromotionSettingsType = typings_1.PromotionSettingsType.FREE_LOWEST_PRICE;
        this.defaultVatOptions = typings_1.VATOptions.NONE;
        this.promotions = [];
        /**
         * Use this flag when client wants to show all active shipping promotion to customer
         * As this will not take selected shipping into account Hence it will result
         * in applying every shipping promotion to cart
         *
         * **Note**
         * Only use this when show cart to show end-customers all possible shipping promotions
         */
        this.isAppliedToAllShipping = false;
        this.shippingRules = [];
        this.additionalDiscount = 0;
        this.managementFee = {
            fee: 0,
            type: typings_1.ManagementFeeType.NONE,
        };
        /**
         * Shipping management fee that calculator will have to
         * take into account when initializing cart
         *
         * ** Note **
         * Shipping management fee will always apply with applied promotion
         * meaning it will use discounted price rather than using subtotal
         * to calculate shippingManagementFee
         */
        this.shippingManagementFee = {
            fee: 0,
            isAppliedPromotion: true,
            type: typings_1.PriceConfigurationType.NONE,
        };
        /**
         * COD management fee that calculator will have to take
         * into account when returning calculated cart
         *
         * ** Note **
         * Cod management fee will always apply with applied promotion
         * meaning it will use discounted price rather than using subtotal
         * to calculate codManagementFee
         */
        this.codManagementFee = {
            fee: 0,
            isAppliedPromotion: true,
            type: typings_1.PriceConfigurationType.NONE,
        };
        /**
         * COD deposit that system will help calculate
         */
        this.codDeposit = {
            fee: 0,
            isAppliedPromotion: true,
            type: typings_1.PriceConfigurationType.NONE,
        };
        /**
         * COD threshold that system will help calculate
         */
        this.codThreshold = {
            fee: 0,
            isAppliedPromotion: true,
            type: typings_1.PriceConfigurationType.NONE,
        };
        this.MINIMUM_NUMBER_IN_RANGE = 0;
        this.defaultSuggestedPromotionSetting = {
            threshold: {
                pricePercent: 50,
                amountPercent: 50,
            },
        };
        this.DEFAULT_RELATED_PROMOTION_CONDITIONS = [
            typings_1.PromotionCondition.BUY_ANY_PRODUCT,
            typings_1.PromotionCondition.BUY_X_PIECE_OF_ANY_PRODUCT,
            typings_1.PromotionCondition.BUY_X_PRICE_IN_TOTAL_OF_ANY_PRODUCT,
        ];
        this.DEFAULT_UP_SELLING_PROMOTION_CRITERIA_LENGTH = 1;
    }
    getRemainingPromotions(appliedPromotionIds, latestProductSKUId) {
        if (!latestProductSKUId) {
            return this.promotions.filter((promotion) => !appliedPromotionIds.includes(promotion.getPromotionId()) ||
                promotion.getPromotionCriteria().length > this.DEFAULT_UP_SELLING_PROMOTION_CRITERIA_LENGTH);
        }
        return this.promotions.filter((promotion) => !appliedPromotionIds.includes(promotion.getPromotionId()) ||
            promotion.getPromotionSelectedProductSKUIds().includes(latestProductSKUId) ||
            promotion.getPromotionCriteria().length > this.DEFAULT_UP_SELLING_PROMOTION_CRITERIA_LENGTH);
    }
    getTotalAppliedDiscountByAppliedPromotionAndProductSKU(promotions, productSKU) {
        return promotions.reduce((discount, appliedPromotion) => {
            if (productSKU.isFree) {
                return discount;
            }
            const promotionTotalDiscount = appliedPromotion.getPromotionTotalDiscount();
            if (!promotionTotalDiscount) {
                return discount;
            }
            return discount + promotionTotalDiscount;
        }, 0);
    }
    getTotalAppliedDiscount(productSKUs) {
        return productSKUs.reduce((totalDiscount, productSKU) => {
            if (productSKU.isFree) {
                return totalDiscount;
            }
            const appliedPromotions = (0, promotion_1.getPromotions)(productSKU.promotions || []);
            const sumAppliedDiscount = this.getTotalAppliedDiscountByAppliedPromotionAndProductSKU(appliedPromotions, productSKU);
            return totalDiscount + sumAppliedDiscount;
        }, 0);
    }
    getTotalPromotionAppliedDiscountOfSelectedProducts(productSKUs, selectedProductIds) {
        return productSKUs.reduce((totalDiscount, productSKU) => {
            if (selectedProductIds.includes(productSKU.id) && !productSKU.isFree) {
                const appliedPromotions = (0, promotion_1.getPromotions)(productSKU.promotions || []);
                const sumAppliedDiscount = appliedPromotions.reduce((discount, appliedPromotion) => {
                    if (productSKU.isFree) {
                        return discount;
                    }
                    const promotionTotalDiscount = appliedPromotion.getPromotionTotalDiscount();
                    if (!promotionTotalDiscount) {
                        return discount;
                    }
                    return discount + promotionTotalDiscount;
                }, 0);
                return totalDiscount + sumAppliedDiscount;
            }
            return totalDiscount;
        }, 0);
    }
    /**
     * Update shipping price with shipping management fee
     * and set shipping management fee
     * @param subtotal
     */
    updateShippingPriceWithManagementFee(subtotal) {
        const { selectedShipping } = this;
        /**
         * Passing in both sub total on both params as we haven't started calculate
         * anything hence there won't be any discount to begin with
         */
        const shippingManagementFee = this.calculateManagementFee(this.shippingManagementFee, subtotal, subtotal);
        // Setting shipping management fee again with fee calculated
        this.setShippingManagementFee(Object.assign(Object.assign({}, this.shippingManagementFee), { fee: shippingManagementFee }));
        /**
         * New total shipping price from quoted shipping price + shipping management fee
         */
        const totalShippingPrice = Number(this.selectedShipping.price) + shippingManagementFee;
        // Setting total shipping price to selected shipping information
        this.selectedShipping = Object.assign(Object.assign({}, selectedShipping), { 
            // Update shipping price here for further calculation
            totalShippingPrice, 
            // Setting original price with mgt fee here to keep track of shipping price
            originalPriceWithManagementFee: totalShippingPrice });
    }
    /**
     * Calculate cod deposit and threshold based on subtotal
     * @param subtotal
     */
    updateCodDepositAndThreshold(subtotal) {
        const depositFee = this.calculateManagementFee(this.codDeposit, subtotal, subtotal);
        const thresholdFee = this.calculateManagementFee(this.codThreshold, subtotal, subtotal);
        // Resetting fee for both deposit and threshold on cod information
        this.setSelectedShippingInformation(Object.assign(Object.assign({}, this.selectedShipping), { codInformation: Object.assign(Object.assign({}, this.selectedShipping.codInformation), { deposit: Object.assign(Object.assign({}, this.codDeposit), { fee: depositFee }), threshold: Object.assign(Object.assign({}, this.codThreshold), { fee: thresholdFee }) }) }));
    }
    /**
     * Update selected shipping information status from deposit
     * @param subtotal
     * @returns {void}
     */
    updateSelectedShippingInformationStatusFromDeposit(subtotal) {
        var _a, _b;
        const thresholdFee = (_b = (_a = this.selectedShipping.codInformation) === null || _a === void 0 ? void 0 : _a.threshold) === null || _b === void 0 ? void 0 : _b.fee;
        if (!thresholdFee) {
            return;
        }
        /**
         * Check whether threshold fee exceeded subtotal or not
         * Threshold fee: 100B
         * Subtotal: 50B
         */
        const isDisabled = thresholdFee > subtotal;
        const disabledInformation = {
            isDisabled,
            disabledReason: isDisabled ? constants_1.ERRORS.THRESHOLD_FEE_EXCEEDED_SUBTOTAL : undefined,
        };
        this.setSelectedShippingInformation(Object.assign(Object.assign({}, this.selectedShipping), disabledInformation));
    }
    // #region Apply remaining amount / price to promotion (up-selling promotion)
    /**
     * Apply remaining product and promotion criteria to bundle promotion
     * @param promotion
     * @param productSKUs
     * @param promotionCriteria
     * @param latestProductSKUId
     * @returns {IPromotion}
     */
    applyRemainingProductAndPromotionCriteriaToBundlePromotion(promotion, productSKUs, promotionCriteria, latestProductSKUId) {
        promotion.setPromotionUpSellingInformationByPromotionCriteria(promotionCriteria);
        // Always prefer matched product sku over largest product sku because it's last one that customer interacts with
        const matchedProductSKU = productSKUs.find((productSKU) => productSKU.id === latestProductSKUId);
        if (matchedProductSKU) {
            return promotion.setPromotionRemainingProductSKUId(matchedProductSKU.id);
        }
        const qualifiedProductSKUForUpSelling = (0, utils_1.getLargestProductSKU)(productSKUs, promotion.getPromotionSelectedProductSKUIds());
        if (qualifiedProductSKUForUpSelling) {
            return promotion.setPromotionRemainingProductSKUId(qualifiedProductSKUForUpSelling.id);
        }
        return promotion;
    }
    /**
     * Apply remaining amount to bundle promotion
     * @param promotion
     * @param productSKUs
     * @param amountPercentThreshold
     * @param latestProductSKUId
     * @returns {IPromotion | null}
     */
    applyRemainingAmountToBundlePromotion(promotion, productSKUs, amountPercentThreshold, latestProductSKUId) {
        const totalAmountOfSelectedProducts = (0, utils_1.getTotalAmountOfSelectedProducts)(productSKUs, promotion.getPromotionSelectedProductSKUIds());
        const promotionCriteria = promotion.getNextPromotionCriteriaByTarget(totalAmountOfSelectedProducts);
        if (!promotionCriteria || !promotionCriteria.minimumThreshold) {
            return promotion;
        }
        const { minimumThreshold } = promotionCriteria;
        const currentAmountPercentage = (totalAmountOfSelectedProducts / minimumThreshold) * this.basePercentage;
        const remainingAmount = (0, utils_1.getNumberInRange)(this.MINIMUM_NUMBER_IN_RANGE, minimumThreshold, minimumThreshold - totalAmountOfSelectedProducts);
        // No product sku matches promotion criteria
        if (amountPercentThreshold > currentAmountPercentage) {
            return null;
        }
        promotion.setPromotionRemainingAmount(remainingAmount);
        return this.applyRemainingProductAndPromotionCriteriaToBundlePromotion(promotion, productSKUs, promotionCriteria, latestProductSKUId);
    }
    /**
     * Apply remaining amount to promotion
     * @param promotion
     * @param productSKUs
     * @param amountPercentThreshold
     * @param latestProductSKUId
     * @returns {IPromotion | null}
     */
    applyRemainingAmountToPromotion(promotion, productSKUs, amountPercentThreshold, latestProductSKUId) {
        const isBundled = promotion.getBundleSetting();
        if (isBundled) {
            return this.applyRemainingAmountToBundlePromotion(promotion, productSKUs, amountPercentThreshold, latestProductSKUId);
        }
        const qualifiedProductSKUs = productSKUs
            .map((productSKU) => {
            const promotionCriteria = promotion.getNextPromotionCriteriaByTarget(productSKU.amount);
            if (!promotionCriteria || !promotionCriteria.minimumThreshold) {
                return undefined;
            }
            const { minimumThreshold } = promotionCriteria;
            const currentAmountPercentage = (productSKU.amount / minimumThreshold) * this.basePercentage;
            const remainingAmount = (0, utils_1.getNumberInRange)(this.MINIMUM_NUMBER_IN_RANGE, minimumThreshold, minimumThreshold - productSKU.amount);
            // Does not meet amount threshold
            if (amountPercentThreshold > currentAmountPercentage) {
                return undefined;
            }
            return Object.assign(Object.assign({}, productSKU), { promotionCriteria,
                remainingAmount });
        })
            .filter(Boolean);
        // No product sku matches promotion criteria
        if (!qualifiedProductSKUs.length) {
            return null;
        }
        let qualifiedProductSKUForUpSelling = qualifiedProductSKUs[0];
        const matchedQualifiedProductSKU = qualifiedProductSKUs.find((qualifiedProductSKU) => qualifiedProductSKU.id === latestProductSKUId);
        if (matchedQualifiedProductSKU) {
            qualifiedProductSKUForUpSelling = matchedQualifiedProductSKU;
        }
        promotion.setPromotionRemainingAmount(qualifiedProductSKUForUpSelling.remainingAmount);
        promotion.setPromotionRemainingProductSKUId(qualifiedProductSKUForUpSelling.id);
        return promotion.setPromotionUpSellingInformationByPromotionCriteria(qualifiedProductSKUForUpSelling.promotionCriteria);
    }
    /**
     * Apply remaining price to total cart promotion
     * @param promotion
     * @param productSKUs
     * @param pricePercentThreshold
     * @returns {IPromotion | null}
     */
    applyRemainingPriceToTotalCartPromotion(promotion, productSKUs, pricePercentThreshold) {
        const totalPrice = (0, utils_1.getTotalPrice)(productSKUs);
        const totalPromotionAppliedDiscount = this.getTotalAppliedDiscount(productSKUs);
        const totalPriceWithDiscount = totalPrice - totalPromotionAppliedDiscount;
        const promotionCriteria = promotion.getNextPromotionCriteriaByTarget(totalPrice);
        if (!promotionCriteria || !promotionCriteria.minimumThreshold) {
            return promotion;
        }
        const { minimumThreshold } = promotionCriteria;
        const currentPricePercentage = (totalPriceWithDiscount / minimumThreshold) * this.basePercentage;
        const remainingPrice = (0, utils_1.getNumberInRange)(this.MINIMUM_NUMBER_IN_RANGE, minimumThreshold, minimumThreshold - totalPriceWithDiscount);
        if (pricePercentThreshold > currentPricePercentage) {
            return null;
        }
        promotion.setPromotionRemainingPrice(remainingPrice);
        return promotion.setPromotionUpSellingInformationByPromotionCriteria(promotionCriteria);
    }
    /**
     * Apply remaining price to bundle promotion
     * @param promotion
     * @param productSKUs
     * @param pricePercentThreshold
     * @param latestProductSKUId
     * @returns {IPromotion | null}
     */
    applyRemainingPriceToBundlePromotion(promotion, productSKUs, pricePercentThreshold, latestProductSKUId) {
        const totalPrice = (0, utils_1.getTotalPriceOfSelectedProducts)(productSKUs, promotion.getPromotionSelectedProductSKUIds()); // total selected products price
        const totalPromotionAppliedDiscount = this.getTotalPromotionAppliedDiscountOfSelectedProducts(productSKUs, promotion.getPromotionSelectedProductSKUIds());
        const totalPriceWithDiscount = totalPrice - totalPromotionAppliedDiscount;
        const promotionCriteria = promotion.getNextPromotionCriteriaByTarget(totalPrice);
        if (!promotionCriteria || !promotionCriteria.minimumThreshold) {
            return null;
        }
        const { minimumThreshold } = promotionCriteria;
        const currentPricePercentage = (totalPriceWithDiscount / minimumThreshold) * this.basePercentage;
        const remainingPrice = (0, utils_1.getNumberInRange)(this.MINIMUM_NUMBER_IN_RANGE, minimumThreshold, minimumThreshold - totalPriceWithDiscount);
        if (pricePercentThreshold > currentPricePercentage) {
            return null;
        }
        promotion.setPromotionRemainingPrice(remainingPrice);
        return this.applyRemainingProductAndPromotionCriteriaToBundlePromotion(promotion, productSKUs, promotionCriteria, latestProductSKUId);
    }
    /**
     * Apply remaining price to promotion
     * @param promotion
     * @param productSKUs
     * @param pricePercentThreshold
     * @param latestProductSKUId
     * @returns {IPromotion | null}
     */
    applyRemainingPriceToPromotion(promotion, productSKUs, pricePercentThreshold, latestProductSKUId) {
        const isTotalCartPricePromotion = promotion.getPromotionSettings().condition === typings_1.PromotionCondition.BUY_X_PRICE_IN_TOTAL_OF_ANY_PRODUCT;
        if (isTotalCartPricePromotion) {
            return this.applyRemainingPriceToTotalCartPromotion(promotion, productSKUs, pricePercentThreshold);
        }
        const isBundled = promotion.getBundleSetting();
        if (isBundled) {
            return this.applyRemainingPriceToBundlePromotion(promotion, productSKUs, pricePercentThreshold, latestProductSKUId);
        }
        const qualifiedProductSKUs = productSKUs
            .map((productSKU) => {
            const totalProductSKUPrice = (0, utils_1.getTotalPrice)([productSKU]);
            const totalProductSKUAppliedDiscount = this.getTotalAppliedDiscount([productSKU]);
            const totalPriceWithDiscount = totalProductSKUPrice - totalProductSKUAppliedDiscount;
            const promotionCriteria = promotion.getNextPromotionCriteriaByTarget(totalProductSKUPrice);
            if (!promotionCriteria || !promotionCriteria.minimumThreshold) {
                return undefined;
            }
            const { minimumThreshold } = promotionCriteria;
            const currentPricePercentage = (totalPriceWithDiscount / minimumThreshold) * this.basePercentage;
            const remainingPrice = (0, utils_1.getNumberInRange)(this.MINIMUM_NUMBER_IN_RANGE, minimumThreshold, minimumThreshold - totalPriceWithDiscount);
            // No product sku matches promotion criteria
            if (pricePercentThreshold > currentPricePercentage) {
                return undefined;
            }
            return Object.assign(Object.assign({}, productSKU), { promotionCriteria,
                remainingPrice });
        })
            .filter(Boolean);
        // No product sku matches promotion criteria
        if (!qualifiedProductSKUs.length) {
            return null;
        }
        let qualifiedProductSKUForUpSelling = qualifiedProductSKUs[0];
        const matchedQualifiedProductSKU = qualifiedProductSKUs.find((qualifiedProductSKU) => qualifiedProductSKU.id === latestProductSKUId);
        if (matchedQualifiedProductSKU) {
            qualifiedProductSKUForUpSelling = matchedQualifiedProductSKU;
        }
        promotion.setPromotionRemainingPrice(qualifiedProductSKUForUpSelling.remainingPrice);
        promotion.setPromotionRemainingProductSKUId(qualifiedProductSKUForUpSelling.id);
        return promotion.setPromotionUpSellingInformationByPromotionCriteria(qualifiedProductSKUForUpSelling.promotionCriteria);
    }
    // #endregion
    /**
     * Calculate options price
     * @param cart
     */
    calculateTotalPriceWithOptionPrice(cart) {
        const totalOptionsPrice = cart.productSKUs.reduce((totalPrice, productSKU) => {
            const optionPrice = (0, utils_1.getTotalSelectedOptionsPrice)(productSKU.options);
            return totalPrice + optionPrice * productSKU.amount;
        }, 0);
        const totalPrice = cart.totalPrice + totalOptionsPrice > 0 ? cart.totalPrice + totalOptionsPrice : 0;
        return (0, utils_1.parseFloatNumberWithDefaultDigit)(totalPrice);
    }
    /**
     * calculate management fee base on type
     * @returns {number}
     */
    calculateManagementFee(managementFee, subtotal, totalPriceWithDiscount) {
        // NONE type should have no management fee
        if (managementFee.type === typings_1.PriceConfigurationType.NONE) {
            return 0;
        }
        if (managementFee.type === typings_1.ManagementFeeType.PERCENTAGE) {
            const managementFeePercent = (0, utils_1.getNumberInRange)(this.minPercentage, this.basePercentage, managementFee.percent);
            // if not applied promotion will using raw subtotal
            if (!managementFee.isAppliedPromotion) {
                return (0, utils_1.parseFloatNumberWithDefaultDigit)((subtotal * managementFeePercent) / this.basePercentage);
            }
            return (0, utils_1.parseFloatNumberWithDefaultDigit)((totalPriceWithDiscount * managementFeePercent) / this.basePercentage);
        }
        // FIXED_PRICE
        return (0, utils_1.parseFloatNumberWithDefaultDigit)(managementFee.fee);
    }
    /**
     * Get cod management fee price from subtotal and discounted total price
     * @param subtotal
     * @param totalPriceWithAdditionalDiscount
     * @returns {number}
     */
    getCodManagementFeePrice(subtotal, totalPriceWithAdditionalDiscount) {
        // Calculate cod management fee price from deprecated management fee object
        if (!this.selectedShipping) {
            const managementFeePrice = this.calculateManagementFee(this.managementFee, subtotal, totalPriceWithAdditionalDiscount);
            // Setting management fee price (deprecated one)
            this.setManagementFee(Object.assign(Object.assign({}, this.managementFee), { fee: managementFeePrice }));
            return managementFeePrice;
        }
        /**
         * If selected shipping is not COD, system can then exit early
         * without calculating anything
         */
        if (!this.selectedShipping.isCod) {
            return 0;
        }
        // Calculate cod management fee price from cod management fee object
        const codManagementFeePrice = this.calculateManagementFee(this.codManagementFee, subtotal, totalPriceWithAdditionalDiscount);
        // Setting cod management fee again with fee calculated
        this.setCodManagementFee(Object.assign(Object.assign({}, this.codManagementFee), { fee: codManagementFeePrice }));
        // Resetting cod management fee again in selected shipping
        this.setSelectedShippingInformation(Object.assign(Object.assign({}, this.selectedShipping), { codInformation: Object.assign(Object.assign({}, this.selectedShipping.codInformation), { managementFee: this.codManagementFee }) }));
        return codManagementFeePrice;
    }
    /**
     * Get total management fee from both shipping management fee and
     * cod shipping management fee
     * @param codManagementFee
     */
    getTotalManagementFee(cart, codManagementFee) {
        var _a;
        const shippingManagementFee = ((_a = cart.shippingManagementFee) === null || _a === void 0 ? void 0 : _a.fee) || 0;
        return shippingManagementFee + codManagementFee;
    }
    /**
     * Calculate cart summary
     * @param cartAfterAppliedShippingRules
     */
    calculateCartSummary(cart) {
        var _a, _b, _c;
        const totalPriceWithOptionPrice = this.calculateTotalPriceWithOptionPrice(cart);
        const totalPriceWithAdditionalDiscount = totalPriceWithOptionPrice > this.additionalDiscount
            ? (0, utils_1.parseFloatNumberWithDefaultDigit)(totalPriceWithOptionPrice - this.additionalDiscount)
            : 0;
        // #region calculate COD management fee
        const codManagementFee = this.getCodManagementFeePrice(cart.subtotal, totalPriceWithAdditionalDiscount);
        // #endregion
        /**
         * Calculate total shipping price based on following information
         *
         * shippingPrice = quotedShippingPrice + shippingManagementFee
         *
         * totalShippingPrice = shippingPrice + totalShippingDiscount + codManagementFee
         *
         * - shippingPrice: calculated from quoted shipping price + shipping mgt price
         * - totalShippingDiscount: calculated discount from shipping promotions
         * - codManagementFee: calculated cod mgt fee
         */
        const totalShippingPriceAfterAppliedPromotions = cart.shippingPrice - cart.totalShippingDiscount + codManagementFee;
        const vatPriceAfterAppliedPromotions = this.getVatPrice(totalPriceWithAdditionalDiscount, totalShippingPriceAfterAppliedPromotions);
        const grandTotal = this.getGrandTotalPrice(totalPriceWithAdditionalDiscount, totalShippingPriceAfterAppliedPromotions, vatPriceAfterAppliedPromotions);
        return Object.assign(Object.assign({}, cart), { additionalDiscount: totalPriceWithOptionPrice > this.additionalDiscount ? this.additionalDiscount : totalPriceWithOptionPrice, codManagementFee: this.codManagementFee, grandTotal: (0, utils_1.parseFloatNumberWithDefaultDigit)(grandTotal), isShippingIncludedInVat: ((_a = this.vat) === null || _a === void 0 ? void 0 : _a.isShippingIncluded) || false, largestDimension: (0, utils_1.getLargestDimension)(cart.productSKUs), managementFee: this.managementFee, 
            /**
             * Update selected shipping price after calculated from promotion,
             * shipping rules + cod mgt fee and etc
             */
            selectedShipping: Object.assign(Object.assign({}, this.selectedShipping), { totalShippingPrice: totalShippingPriceAfterAppliedPromotions }), shippingPrice: (0, utils_1.parseFloatNumberWithDefaultDigit)(cart.shippingPrice), totalManagementFee: this.getTotalManagementFee(cart, codManagementFee), totalPrice: totalPriceWithAdditionalDiscount, totalProductDiscount: (0, utils_1.parseFloatNumberWithDefaultDigit)(cart.totalProductDiscount), totalShippingDiscount: (0, utils_1.parseFloatNumberWithDefaultDigit)(cart.totalShippingDiscount), totalShippingPrice: totalShippingPriceAfterAppliedPromotions, totalWeight: (0, utils_1.getTotalWeight)(cart.productSKUs), vatOptions: ((_b = this.vat) === null || _b === void 0 ? void 0 : _b.options) || typings_1.VATOptions.NONE, vatPrice: (0, utils_1.parseFloatNumberWithDefaultDigit)(vatPriceAfterAppliedPromotions), vatRate: ((_c = this.vat) === null || _c === void 0 ? void 0 : _c.rate) || 7 });
    }
    /**
     * Calculate cart with promotion
     * @param cart
     * @param promotions
     */
    calculateCartWithPromotions(cart, promotions) {
        return promotions.reduce((cartWithPromotions, promotion) => promotion.applyPromotion(cartWithPromotions), cart);
    }
    /**
     * Calculate cart with shipping rule if there are no shipping rules
     * then return initial cart
     */
    calculateCartWithShippingRules(cart) {
        var _a, _b, _c, _d;
        /**
         * Backward compat by checking for selected shipping object first before falling back to deprecated one
         *
         * e.g.
         *
         *
         * Total shipping price: quoted shipping price + shipping mft fee
         *
         *
         * Shipping rate (deprecated): quoted shipping price from legacy shipping
         */
        const shippingPrice = ((_a = this.selectedShipping) === null || _a === void 0 ? void 0 : _a.totalShippingPrice) || ((_b = this.shipping) === null || _b === void 0 ? void 0 : _b.rate) || 0;
        if (!this.shippingRules.length) {
            return Object.assign(Object.assign({}, cart), { shippingPrice });
        }
        const shippingRulesProperties = this.shippingRules.map((shippingRule) => shippingRule.getProperties());
        /**
         * Backward compat by fetching for selected shipping first
         * before falling back to shipping method
         */
        const selectedShippingId = (_c = this.selectedShipping) === null || _c === void 0 ? void 0 : _c.shippingId;
        const shippingMethod = (_d = this.shipping) === null || _d === void 0 ? void 0 : _d.method;
        if (!selectedShippingId && !shippingMethod) {
            return Object.assign(Object.assign({}, cart), { shippingPrice });
        }
        let everyProductSKUsHasShippingRule = false;
        if (selectedShippingId) {
            everyProductSKUsHasShippingRule = (0, utils_1.hasShippingRuleInEveryProductSKUsBySelectedShippingId)(cart.productSKUs, shippingRulesProperties, selectedShippingId);
        }
        else {
            everyProductSKUsHasShippingRule = (0, utils_1.hasShippingRuleInEveryProductSKUs)(cart.productSKUs, shippingRulesProperties, shippingMethod);
        }
        return this.shippingRules.reduce((cartWithShippingRules, shippingRule) => shippingRule.applyShippingRule(cartWithShippingRules), Object.assign(Object.assign({}, cart), { shippingPrice: !everyProductSKUsHasShippingRule ? shippingPrice : 0 }));
    }
    /**
     * Get grand total price
     * @param totalPrice
     * @param vatPrice
     * @returns {number}
     */
    getGrandTotalPrice(totalPrice, totalShippingPrice, vatPrice) {
        var _a;
        const vatOptions = ((_a = this.vat) === null || _a === void 0 ? void 0 : _a.options) || this.defaultVatOptions;
        // For case where VAT options is excluded
        if (vatOptions === typings_1.VATOptions.EXCLUDED) {
            return (0, utils_1.getFloatValueWithFixedDigit)(totalPrice + totalShippingPrice + vatPrice);
        }
        // Normal case
        return (0, utils_1.getFloatValueWithFixedDigit)(totalPrice + totalShippingPrice);
    }
    /**
     * Get initial cart
     * @returns {Cart}
     */
    getInitialCart() {
        var _a;
        const productSKUs = this.getProductSKUs();
        // chosenShipping can be undefined
        const chosenShipping = this.shipping
            ? Object.assign({}, this.shipping)
            : undefined;
        const subtotal = (0, utils_1.getTotalPrice)(productSKUs);
        /**
         * Calculate total shipping price from quoted shipping price
         * and management fee that sometime requires subtotal in order to
         * calculate it
         */
        const { selectedShipping } = this;
        if (selectedShipping && this.shippingManagementFee) {
            this.updateShippingPriceWithManagementFee(subtotal);
        }
        /**
         * Calculate threshold for selected shipping that
         * requires subtotal in order to calculate it
         */
        if (selectedShipping && selectedShipping.isCod && selectedShipping.codInformation) {
            this.updateCodDepositAndThreshold(subtotal);
            this.updateSelectedShippingInformationStatusFromDeposit(subtotal);
        }
        /**
         * Calculate whether shipping is cod or not and
         * if yes, calculator will proceed to validate whether selected shipping should be enabled or disabled
         * if no, calculator will ignore this
         */
        return {
            productSKUs,
            subtotal,
            additionalDiscount: this.additionalDiscount,
            isAppliedToAllShipping: this.isAppliedToAllShipping || false,
            largestDimension: [0, 0, 0],
            managementFee: this.managementFee,
            promotions: [],
            selectedShipping: this.selectedShipping,
            shipping: chosenShipping,
            shippingDuration: (0, utils_1.getMaximumShippingDuration)((_a = this.selectedShipping) === null || _a === void 0 ? void 0 : _a.duration),
            shippingManagementFee: this.shippingManagementFee,
            shippingPrice: 0,
            shippingRules: [],
            totalPrice: subtotal,
            totalProductDiscount: 0,
            totalShippingDiscount: 0,
            totalShippingPrice: 0,
            totalWeight: 0,
        };
    }
    getRelatedPromotionByProductSKUIds(productSKUId, promotions) {
        return promotions.filter((promotion) => {
            const { condition } = promotion.getPromotionSettings();
            // BUY_ANY_PRODUCT and BUY_X_PIECE_OF_ANY_PRODUCT and BUY_X_PRICE_IN_TOTAL_OF_ANY_PRODUCT are alway included
            if (this.DEFAULT_RELATED_PROMOTION_CONDITIONS.includes(condition)) {
                return true;
            }
            const selectedProductSKUIds = promotion.getPromotionSelectedProductSKUIds();
            // this promotion apply all product
            if (!selectedProductSKUIds.length) {
                return true;
            }
            const relatedPromotion = selectedProductSKUIds.find((selectedProductSKUId) => selectedProductSKUId === productSKUId);
            return !!relatedPromotion;
        });
    }
    /**
     * Get total vat price
     * @param totalProductPrice
     * @param totalShippingPrice
     * @returns {number}
     */
    getVatPrice(totalProductPrice, totalShippingPrice) {
        var _a, _b, _c;
        const vatOptions = ((_a = this.vat) === null || _a === void 0 ? void 0 : _a.options) || this.defaultVatOptions;
        const vatRate = ((_b = this.vat) === null || _b === void 0 ? void 0 : _b.rate) || 7;
        const isShippingIncluded = ((_c = this.vat) === null || _c === void 0 ? void 0 : _c.isShippingIncluded) || false;
        const totalPrice = isShippingIncluded ? totalProductPrice + totalShippingPrice : totalProductPrice;
        if (vatOptions === typings_1.VATOptions.EXCLUDED) {
            return (totalPrice * vatRate) / this.basePercentage;
        }
        const grossAmount = totalPrice / (this.basePercentage + vatRate);
        return grossAmount * vatRate;
    }
    /**
     * Calculate everything from given information
     */
    calculate() {
        const cart = this.getInitialCart();
        const [freeablePromotions, nonFreeablePromotions] = (0, utils_1.separateFreeablePromotionsAndOtherPromotions)(this.promotions);
        const [shippingPromotions, productPromotions] = (0, utils_1.separateShippingPromotionsAndOtherPromotions)(nonFreeablePromotions);
        const filteredDuplicatedPromotions = (0, utils_1.filterDuplicatedPromotionsByPromotionSettingsType)(productPromotions, this.defaultOneTimeAppliedPromotionSettingsType);
        const sortedProductPromotions = (0, utils_1.sortPromotionsForApplyToCart)(filteredDuplicatedPromotions);
        // apply product promotions
        const cartWithProductPromotions = this.calculateCartWithPromotions(cart, sortedProductPromotions);
        /**
         * NOTE
         * in order to correct apply promotion with awardTotalPrice (FreeShippingPromotion and FreeGiveawayPromotion)
         * Those promotions need to apply after other promotion applied
         */
        let cartWithFreeProducts = cartWithProductPromotions;
        // apply free item promotions
        if (freeablePromotions.length) {
            cartWithFreeProducts = this.calculateCartWithPromotions(cartWithProductPromotions, freeablePromotions);
        }
        // apply shipping rules
        const cartWithShippingRules = this.calculateCartWithShippingRules(cartWithFreeProducts);
        const sortedShippingPromotions = (0, utils_1.sortPromotionsForApplyToCart)(shippingPromotions);
        // apply shipping promotions
        const cartWithShippingPromotions = this.calculateCartWithPromotions(cartWithShippingRules, sortedShippingPromotions);
        return this.calculateCartSummary(cartWithShippingPromotions);
    }
    /**
     * Set additional discount from admin
     * @param additionalDiscount
     * @returns {this}
     */
    setAdditionalDiscount(additionalDiscount) {
        this.additionalDiscount = (0, utils_1.parseFloatNumberWithDefaultDigit)(additionalDiscount);
        return this;
    }
    /**
     * Set management fee from admin
     * @deprecated
     * Use either "setCodManagementFee" or "setShippingManagementFee"
     * @param managementFee
     * @returns {this}
     */
    setManagementFee(managementFee) {
        this.managementFee = Object.assign(Object.assign({}, managementFee), { fee: (0, utils_1.parseFloatNumberWithDefaultDigit)(managementFee.fee) });
        return this;
    }
    /**
     * Set shipping management fee for shipping
     * @param shippingManagementFee
     * @returns {this}
     */
    setShippingManagementFee(shippingManagementFee) {
        this.shippingManagementFee = shippingManagementFee;
        return this;
    }
    // #region Cod information related
    /**
     * Set cod deposit for cod information
     * @param codDeposit
     * @returns {this}
     */
    setCodDeposit(codDeposit) {
        this.codDeposit = codDeposit;
        return this;
    }
    /**
     * Set cod management fee for cod information
     * @param codManagementFee
     * @returns {this}
     */
    setCodManagementFee(codManagementFee) {
        this.codManagementFee = codManagementFee;
        return this;
    }
    /**
     * Set cod threshold for cod information
     * @param codThreshold
     * @returns {this}
     */
    setCodThreshold(codThreshold) {
        this.codThreshold = codThreshold;
        return this;
    }
    // #endregion
    /**
     * Set promotions that are applicable with products
     * @param promotionsToBeApplied
     * @returns {this}
     */
    setBulkPromotion(promotionsToBeApplied) {
        this.promotions = this.promotions.concat(promotionsToBeApplied);
        return this;
    }
    /**
     * Set promotions with promotions' properties that are applicable with products
     * @param promotionsProperties
     * @returns {this}
     */
    setBulkPromotionWithPromotionProperties(promotionsProperties) {
        const promotionsToBeApplied = (0, promotion_1.getPromotions)(promotionsProperties);
        this.promotions = this.promotions.concat(promotionsToBeApplied);
        return this;
    }
    /**
     * Set promotion that is applicable with products
     * @param promotionToBeApplied
     * @returns {this}
     */
    setPromotion(promotionToBeApplied) {
        this.promotions = this.promotions.concat([promotionToBeApplied]);
        return this;
    }
    /**
     * @deprecated
     * Set shipping's information
     * @param chosenShipping
     * @returns {this}
     */
    setShipping(chosenShipping) {
        this.shipping = chosenShipping;
        return this;
    }
    /**
     * Set selected shipping information
     * @param selectedShipping
     * @returns {this}
     */
    setSelectedShippingInformation(selectedShipping) {
        // Setting selected shipping to shipping information
        this.selectedShipping = selectedShipping;
        // Set shipping management fee for selected shipping
        if (selectedShipping.managementFee) {
            this.setShippingManagementFee(Object.assign(Object.assign({}, this.shippingManagementFee), selectedShipping.managementFee));
        }
        // Set cod setting if selected shipping is cod
        if (selectedShipping.isCod) {
            // Set COD management fee for selected shipping
            if (selectedShipping.codInformation && selectedShipping.codInformation.managementFee) {
                this.setCodManagementFee(Object.assign(Object.assign({}, this.codManagementFee), selectedShipping.codInformation.managementFee));
            }
            // Set COD deposit
            if (selectedShipping.codInformation && selectedShipping.codInformation.deposit) {
                this.setCodDeposit(Object.assign(Object.assign({}, this.codDeposit), selectedShipping.codInformation.deposit));
            }
            // Set COD threshold
            if (selectedShipping.codInformation && selectedShipping.codInformation.threshold) {
                this.setCodThreshold(Object.assign(Object.assign({}, this.codThreshold), selectedShipping.codInformation.threshold));
            }
        }
        return this;
    }
    /**
     * Set is applied to all shipping
     *
     * ** Note **
     *
     * Use this flag only when system wants calculator to show all
     * possible shipping promotion that is available to customer
     * @returns {this}
     */
    setIsAppliedToAllShipping() {
        this.isAppliedToAllShipping = true;
        return this;
    }
    /**
     * Set shipping rule with an assumption of having only shipping rule that is the
     * same with selected shipping method
     * @param shippingRulesProperties
     * @returns {this}
     */
    setBulkShippingRuleWithShippingRuleProperties(shippingRulesProperties) {
        const shippingRulesToBeApplied = (0, shipping_rule_1.getShippingRules)(shippingRulesProperties);
        this.shippingRules = this.shippingRules.concat(shippingRulesToBeApplied);
        return this;
    }
    /**
     * Set VAT's settings
     * @param vatSetting
     * @returns {this}
     */
    setVat(vatSetting) {
        this.vat = vatSetting;
        return this;
    }
    /**
     * Get suggested promotions
     * @param setting
     * @param latestProductSKUId
     * @returns {ReadonlyArray<BasePromotionProperties>}
     */
    getSuggestedPromotions(setting, latestProductSKUId) {
        const cartSummary = this.calculate();
        const appliedPromotions = cartSummary.promotions;
        const appliedPromotionIds = appliedPromotions.map((promotion) => promotion.id);
        const remainingPromotions = this.getRemainingPromotions(appliedPromotionIds, latestProductSKUId);
        const suggestedPromotionSetting = setting || this.defaultSuggestedPromotionSetting;
        // percentage 0 - 100
        const pricePercentThreshold = (0, utils_1.getNumberInRange)(this.minPercentage, this.basePercentage, suggestedPromotionSetting.threshold.pricePercent);
        const amountPercentThreshold = (0, utils_1.getNumberInRange)(this.minPercentage, this.basePercentage, suggestedPromotionSetting.threshold.amountPercent);
        const { promotionSettingProperties } = suggestedPromotionSetting;
        const filteredNotAppliedPromotions = remainingPromotions
            .map((promotion) => {
            const promotionSetting = promotion.getPromotionSettings();
            // promotionSettingProperties is undefined mean allow all promotion
            if (promotionSettingProperties) {
                // empty array mean disable all promotion setting
                if (!promotionSettingProperties.length) {
                    return null;
                }
                const supportedPromotion = promotionSettingProperties.find((promotionSettingProperty) => {
                    const isMatchedPromotionType = promotionSettingProperty.promotionType === promotion.getPromotionType();
                    const isMatchedSettingType = promotionSettingProperty.settingsType === promotion.getPromotionSettingsType();
                    // if conditions is undefined mean allow all conditions
                    const isMatchedCondition = promotionSettingProperty.conditions
                        ? promotionSettingProperty.conditions.includes(promotionSetting.condition)
                        : true;
                    return isMatchedPromotionType && isMatchedSettingType && isMatchedCondition;
                });
                if (!supportedPromotion) {
                    return null;
                }
            }
            const promotionCriteria = promotion.getPromotionCriteria();
            if (!promotionCriteria.length) {
                return promotion;
            }
            const hasAmountThreshold = promotion.hasAmountThresholdInPromotionCriteria();
            const hasPriceThreshold = promotion.hasPriceThresholdInPromotionCriteria();
            if (!hasAmountThreshold && !hasPriceThreshold) {
                return null;
            }
            if (hasPriceThreshold) {
                return this.applyRemainingPriceToPromotion(promotion, cartSummary.productSKUs, pricePercentThreshold, latestProductSKUId);
            }
            return this.applyRemainingAmountToPromotion(promotion, cartSummary.productSKUs, amountPercentThreshold, latestProductSKUId);
        })
            .filter(Boolean);
        const sortedPromotions = (0, utils_1.sortPromotionsByPriorityFromHightToLow)(filteredNotAppliedPromotions);
        return sortedPromotions.map((sortedPromotion) => sortedPromotion.toBasePromotionJSON());
    }
    getProductSKUDiscountSummary(productSKUs, promotionProperties = []) {
        if (!productSKUs || !productSKUs.length) {
            return [];
        }
        const promotions = (0, promotion_1.getPromotions)(promotionProperties);
        // filter not bundled and only product promotion
        const filteredPromotions = promotions.filter((promotion) => !promotion.getPromotionSettings().isBundled && promotion.getPromotionType() === typings_1.PromotionType.PRODUCT);
        return productSKUs.map((productSKU) => {
            // applied promotion
            const appliedPromotions = this.getRelatedPromotionByProductSKUIds(productSKU.id, filteredPromotions);
            const sumAppliedDiscount = this.getTotalAppliedDiscountByAppliedPromotionAndProductSKU(appliedPromotions, productSKU);
            const totalPrice = productSKU.price * productSKU.amount;
            const totalDiscount = (0, utils_1.getNumberInRange)(this.MINIMUM_NUMBER_IN_RANGE, totalPrice, sumAppliedDiscount); // 0 - total price
            // max discount price is total price
            const discountedPrice = totalPrice - totalDiscount;
            const discountedUnitPrice = discountedPrice > 0 ? discountedPrice / productSKU.amount : 0;
            return {
                discountedPrice,
                discountedUnitPrice,
                totalDiscount,
                totalPrice,
                amount: productSKU.amount,
                defaultPrice: productSKU.price,
                productSKUId: productSKU.id,
                promotions: appliedPromotions.map((appliedPromotion) => appliedPromotion.toBasePromotionJSON()),
            };
        });
    }
}
exports.ShoppingCartCalculator = ShoppingCartCalculator;
//# sourceMappingURL=index.js.map